import {
  AreaFragmentCombinationDeclarationGroupDto,
  AreaFragmentDto,
  AreaType,
  ClientConfigurationDormerSpecificSettingsClientVariableSettingsDto,
  ConfigurationTemplateDto, FaceConfigurationDto,
  FrameDto,
  OrderDto,
  SectionFragmentDto,
  SectionFragmentSide,
  SectionFragmentType
} from "@hec/api-dtos";
import {
  generateGuid,
  GetAreaFragmentsFromFace,
  GetSectionFragmentByTypeFromFace,
  GetSectionFragmentsByTypeFromFace,
  getSelectableSectionFragmentById,
  getSouthFaceFromOrder
} from "./Util";
import {DormerRootState} from "../DormerRootState";
import {
  applyMostSuitableAreaFragmentCombinationDeclarationGroupAction,
  applyPendingAreaFragmentMutationAction,
  applyPendingWindowInsetFragmentMutationAction,
  applySelectedAreaFragmentCombinationDeclarationGroupAction,
  changeAmountOfAreaFragmentAction,
  changeAmountOfAreaFragmentFramesAction,
  frameRedistributeToAreaFragmentsAction,
  PendingMutationType
} from "./AreaFragmentActions";
import {getDefaultAreaFragment, getDefaultFrameSelector} from "../SelectableSectionFragments";
import {replaceOrderSectionFragmentsAction, SectionFragmentMutateViewModel} from "./FrameActions";
import {call, put, select, takeLatest} from "redux-saga/effects";
import {GetFaceConfigurationWidthFromOrder} from "./Util/GetFaceConfigurationWidthFromOrder";
import {
  getAreaFragmentHeight,
  getCheeksWidth,
  getEastSplinterWidth,
  getFrameSurroundingRodThickness,
  GetTotalWidthOfNonAreaFragments,
  getWindowInsetHeight
} from "@hec/core";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import _ from "lodash";
import {getClientConfigurationDormerSpecificSettingsClientVariableSettingsDto} from "../ClientInformation";
import {getSelectedConfigurationTemplateSelector} from "../ConfigurationTemplates";
import {MakeGptInspectable} from "@hec/utils";
import {computeProportionalWidths, computeWidths} from './AreaFragmentSagasHelpers';
import {getFaceConfiguration} from "./FaceConfigurationSelectors";

export const DEFAULT_FILLER_WIDTH = 200;


// 😜
export const makeSectionFragmentMutateViewModel = (sf: SectionFragmentDto, deleteMe = false, dirty = true): SectionFragmentMutateViewModel => {
  return _.clone({
    ...sf,
    dirty,
    deleteMe
  })
}


const createFrameFromDefaultWithId = (newFrameId: string, faceId: string, currentDefaultFrame: FrameDto, order: OrderDto, sort: number, ctdsscvs: ClientConfigurationDormerSpecificSettingsClientVariableSettingsDto) => {
  const windowInsetHeight = getWindowInsetHeight(order, ctdsscvs);

  const newFrame: FrameDto = {
    ...currentDefaultFrame,

    id: newFrameId,
    tempId: newFrameId,
    copiedFrom: currentDefaultFrame,
    copiedFromId: currentDefaultFrame?.id ?? null,
    variation: currentDefaultFrame.variation,
    sort,
    organisationId: null,
    orderId: order.id,
    configurationTemplateId: null,
    faceConfigurationId: order.faceConfiguration.id,
    isOrganisationAddable: false,
    isDefaultForTemplate: false,
    isUserSelectable: false,
    isSelectableFrame: false,
    belongsToUser: true,
    faceId: faceId,

    // Bunch off boilerplate (i gave up being explicit halfway through and caved in by adding the spread operator at the top)
    // TODO: Ideally updating the AreaFragmentDto type will cause a compilation error here, forcing the implementing dev to do a fix
    // Maybe also move to mapper??? (prototyping fase rn)
    height: windowInsetHeight,
    sectionFragmentType: SectionFragmentType.Frame,


    // availableDormerMaterialIds: [],
    // availableDormerMaterials: [],

    // TODO: check selection for matching modifygroup, use that instead of default color
    // configuredDormerColorId: null,
    // configuredDormerColor: null,
    priceCalculationStrategyId: null,
    priceCalculationStrategy: null,
    minWidth: null,
    maxWidth: null,
    configuredDormerColor: currentDefaultFrame.configuredDormerColor,
    configuredDormerColorId: currentDefaultFrame.configuredDormerColorId,


    // Will need to be changed to support overlays.
    areaType: AreaType.Frame,

    // Recalculate me :)
    locationX: 0,
    locationY: 0,
    width: 0,
  };
  return _.clone(newFrame);
}

const createAreaFragmentFromDefaultWithId = (
  currentDefaultAreaFragment: AreaFragmentDto,
  newAreaFragmentId: string,
  order: OrderDto,
  sort: number,
  faceId: string,
): AreaFragmentDto => {
  const areaFragmentHeight = getAreaFragmentHeight(order);

  const newAreaFragment: AreaFragmentDto = {
    ...currentDefaultAreaFragment,
    id: newAreaFragmentId,
    tempId: newAreaFragmentId,
    copiedFrom: currentDefaultAreaFragment,
    copiedFromId: currentDefaultAreaFragment?.id ?? null,
    sort,
    organisationId: null,
    orderId: order.id,
    configurationTemplateId: null,
    faceConfigurationId: order.faceConfiguration.id,
    isOrganisationAddable: false,
    isDefaultForTemplate: false,
    isUserSelectable: false,
    belongsToUser: true,
    faceId: faceId,
    childSectionFragmentsIds: [],
    childSectionFragments: [],

    // Bunch off boilerplate (i gave up being explicit halfway through and caved in by adding the spread operator at the top)
    // TODO: Ideally updating the AreaFragmentDto type will cause a compilation error here, forcing the implementing dev to do a fix
    height: areaFragmentHeight,
    sectionFragmentType: SectionFragmentType.AreaFragment,
    availableDormerMaterialIds: currentDefaultAreaFragment.availableDormerMaterialIds,
    availableDormerMaterials: currentDefaultAreaFragment.availableDormerMaterials,
    configuredDormerColorId: currentDefaultAreaFragment.configuredDormerColorId,
    configuredDormerColor: currentDefaultAreaFragment.configuredDormerColor,
    priceCalculationStrategyId: null,
    priceCalculationStrategy: null,
    minWidth: null,
    maxWidth: null,
    variation: null,

    // Will need to be changed to support overlays.
    areaType: AreaType.Frame,

    // Recalculate me :)
    locationX: 0,
    locationY: 0,
    width: 0,
  };

  return newAreaFragment;
};


function* ChangeAmountAreaFragmentSaga(action: ReturnType<typeof changeAmountOfAreaFragmentAction>) {
  let mutatedSectionFragments: SectionFragmentMutateViewModel[] = [];

  const state: DormerRootState = yield select((state: DormerRootState) => state);
  const clientVariables: ClientConfigurationDormerSpecificSettingsClientVariableSettingsDto = getClientConfigurationDormerSpecificSettingsClientVariableSettingsDto(state);
  const order: OrderDto | null = state.order.order;
  if (!order) {
    return;
  }

  const faceConfigurationWidth = GetFaceConfigurationWidthFromOrder(order) ?? 0;

  const face = getSouthFaceFromOrder(order);
  if (!face) {
    return;
  }


  const totalWidthOfNonAreaFragments = GetTotalWidthOfNonAreaFragments(face) ?? 0;
  const totalAreaFragmentWidth = faceConfigurationWidth - totalWidthOfNonAreaFragments;


  const faceId = face.id;
  const areaFragments = GetAreaFragmentsFromFace(face);
  const faceFrames = (GetSectionFragmentsByTypeFromFace(face, SectionFragmentType.Frame) as FrameDto[]);


  const currentDefaultAreaFragment = getDefaultAreaFragment(state);
  if (!currentDefaultAreaFragment) {
    return;
  }

  const currentDefaultFrame = getDefaultFrameSelector(state);
  if (!currentDefaultFrame) {
    return;
  }

  const currentAmountOfAreaFragments = areaFragments.length;
  const toCreateAmount = action.payload.amount - currentAmountOfAreaFragments;

  let replacementAreaFragments: AreaFragmentDto[] = [...(areaFragments.map(x => _.clone(x)))];
  let replacementFrames: FrameDto[] = [...faceFrames];


  let deleteMeSfIds: string[] = [];


  if (toCreateAmount > 0) {

    mutatedSectionFragments = [...areaFragments, ...faceFrames].map(x => makeSectionFragmentMutateViewModel(x, false, false));
    console.log("toCreateAmount", toCreateAmount);

    for (let i = 0; i < toCreateAmount; i++) {

      const newFrameId = generateGuid();
      const newFrame = createFrameFromDefaultWithId(newFrameId, faceId, currentDefaultFrame, order, i, clientVariables);

      replacementFrames = [...replacementFrames, newFrame];


      const newAreaFragmentId = generateGuid();

      const newAreaFragment = createAreaFragmentFromDefaultWithId(
        currentDefaultAreaFragment,
        newAreaFragmentId,
        order,
        currentAmountOfAreaFragments + i,
        faceId
      );

      newAreaFragment.childSectionFragmentsIds = [newFrameId];
      newAreaFragment.childSectionFragments = [newFrame];
      replacementAreaFragments = [...replacementAreaFragments, newAreaFragment];


      mutatedSectionFragments = [
        ...mutatedSectionFragments,
        makeSectionFragmentMutateViewModel(newFrame),
        makeSectionFragmentMutateViewModel(newAreaFragment),
      ]

    }
  } else if (toCreateAmount < 0) {
    mutatedSectionFragments = [
      ...replacementAreaFragments.map(x => makeSectionFragmentMutateViewModel(x, false, false),
        ...faceFrames.map(x => makeSectionFragmentMutateViewModel(x, false, false)))
    ];

    const framesTestPre = mutatedSectionFragments.filter(x => x.sectionFragmentType === SectionFragmentType.Frame);
    const areaFragmentsTestPre = mutatedSectionFragments.filter(x => x.sectionFragmentType === SectionFragmentType.AreaFragment);

    const removeAmount = -toCreateAmount;
    for (let i = 0; i < removeAmount; i++) {
      const removeMe = replacementAreaFragments.sort((a, b) => b.locationX - a.locationX).find(x => !!x)!;

      const removeMeFrameIds = removeMe.childSectionFragmentsIds ?? [];

      mutatedSectionFragments = [
        ...(mutatedSectionFragments.filter(x => x.id !== removeMe.id)),
        // Lets let them be orphaned and handle it later
        // ...replacementFrames.filter(x => removeMeFrameIds.indexOf(x?.id ?? x?.tempId ?? 'no-id :(') > 0)
        //   .map(x => makeSectionFragmentMutateViewModel(x, true)),
        makeSectionFragmentMutateViewModel(removeMe, true),
        ...(replacementFrames
          .filter(x => removeMeFrameIds.indexOf(x?.id ?? x?.tempId ?? 'no-id :(') !== -1)
          .map(removeMe => makeSectionFragmentMutateViewModel(removeMe, true)))
      ];
      // replacementFrames = [
      //   ...replacementFrames
      //     .filter(x => removeMeFrameIds.indexOf(x?.id ?? x?.tempId ?? 'no-id :(') === -1)
      // ];
      replacementAreaFragments = replacementAreaFragments.filter(x => x !== removeMe);
    }
  }


  const amountOfFillers = replacementAreaFragments.filter(x => x.areaType === AreaType.Filler).length;
  const amountOfNonFillers = replacementAreaFragments.filter(x => x.areaType !== AreaType.Filler).length;

  const reservedFillerWidth = amountOfFillers * DEFAULT_FILLER_WIDTH;

  const distributeWidth = totalAreaFragmentWidth - reservedFillerWidth;


  const widths: number[] = computeWidths(distributeWidth, amountOfNonFillers);

  // // 1) How many mm does each item get if we do an integer division?
  // const baseWidth = Math.floor(distributeWidth / amountOfNonFillers);
  //
  // // 2) Fill the first n-2 items with that quotient
  // for (let i = 0; i < amountOfNonFillers - 2; i++) {
  //   widths.push(baseWidth);
  // }
  //
  // // 3) Calculate the leftover after those n-2 items
  // const sumSoFar = widths.reduce((a, b) => a + b, 0);
  // const leftover = distributeWidth - sumSoFar;
  //
  // // 4) Split the leftover between the last two items
  // //    — “round” the second-to-last, and let the very last soak up the remainder
  // const secondToLast = Math.floor(leftover / 2);
  // const last = leftover - secondToLast;
  //
  // widths.push(secondToLast, last);

  const xOffset = Math.round((getEastSplinterWidth(face) ?? 0) +
    (getCheeksWidth(face, SectionFragmentSide.East) ?? 0));

  const replacementAreaFragmentsWidths: number[] = [];

  replacementAreaFragments.forEach((x, i) => {
    try {
      let mutationModel = mutatedSectionFragments.find(y => y.id === x.id);

      // const mutationModelIndex = mutatedSectionFragments.indexOf(mutationModel);

      // in case this was a previously existing frame that we need to update the width and location off.
      // we are just now finding out this is in fact one that needs a mutation
      if (!mutationModel) {
        mutationModel = makeSectionFragmentMutateViewModel(areaFragments.find(y => y.id === x.id)!);
        mutatedSectionFragments.push(mutationModel);
      }

      mutationModel.sort = i;
      if (mutationModel.areaType === AreaType.Filler) {
        mutationModel.width = DEFAULT_FILLER_WIDTH;
      } else {
        mutationModel.width = widths[i]
      }
      x.width = mutationModel.width;

      replacementAreaFragmentsWidths.push(x.width);

      mutationModel.locationX = replacementAreaFragmentsWidths
        .slice(0, i)
        .reduce((sum, mm) => sum + mm, 0);
      mutationModel.locationX += xOffset

      // We cloned with lowdash if we made dirty so lets hard replace
      // mutatedSectionFragments[mutationModelIndex] = mutationModel;
    } catch (e) {
      console.error(e);
    }
  });


  const touchedSectionFragments = [
    SectionFragmentType.Frame,
    SectionFragmentType.AreaFragment,
  ];

  const nonImpactedSectionFragments: SectionFragmentMutateViewModel[] = face.sectionFragments
    .filter(x => touchedSectionFragments.indexOf(x.sectionFragmentType) === -1)
    .map(x => {
      return {
        ...x,
        dirty: false
      }
    });

  /// The miracle of childbirth(this shouldn't really happen once we have reimplemented the way predefined layouts work
  const allIds = new Set(mutatedSectionFragments.map(sf => sf.id));
  const noChildren = mutatedSectionFragments.filter(sf =>
    sf.sectionFragmentType === SectionFragmentType.AreaFragment &&
    !sf.childSectionFragmentsIds.some(childId => allIds.has(childId))
  );
  noChildren.forEach(x => {
    const newFrameId = generateGuid();
    const newFrame = createFrameFromDefaultWithId(newFrameId, faceId, currentDefaultFrame, order, 0, clientVariables);

    x.childSectionFragmentsIds = [newFrameId];
    x.childSectionFragments = [newFrame];

    mutatedSectionFragments.push(makeSectionFragmentMutateViewModel(newFrame))
  });

  const sectionFragments = [...nonImpactedSectionFragments, ...mutatedSectionFragments];

  const newSectionFragmentIds = sectionFragments.map(x => x.id);
  const duplicatIdSectionFragments = sectionFragments.filter(x => newSectionFragmentIds.filter(id => x.id == id).length > 1);

  if (duplicatIdSectionFragments.length > 0) {
    console.error("DUPLICATE ID REPLACEMENT ERR!");
  }

  const framesTest = sectionFragments.filter(x => x.sectionFragmentType === SectionFragmentType.Frame);
  const areaFragmentsTest = sectionFragments.filter(x => x.sectionFragmentType === SectionFragmentType.AreaFragment);

  yield put(replaceOrderSectionFragmentsAction({
    sectionFragments,
    // sectionFragments: [...nonImpactedSectionFragments, ...mutatedSectionFragments],
    redistributeFrames: true,
  }));
}


function* FrameRedistributeToAreaFragmentsSaga(action: ReturnType<typeof frameRedistributeToAreaFragmentsAction>) {

  const state: DormerRootState = yield select((state: DormerRootState) => state);
  const order: OrderDto | null = state.order.order;
  if (!order) {
    return;
  }
  const faceConfigurationWidth = GetFaceConfigurationWidthFromOrder(order) ?? 0;

  const face = getSouthFaceFromOrder(order);
  if (!face) {
    return;
  }

  const clientVariables: ClientConfigurationDormerSpecificSettingsClientVariableSettingsDto = yield select(getClientConfigurationDormerSpecificSettingsClientVariableSettingsDto);

  const payloadSectionFragments = action.payload?.sectionFragments;
  const faceFrames = payloadSectionFragments == null ?
    (GetSectionFragmentsByTypeFromFace(face, SectionFragmentType.Frame) as FrameDto[])
    : payloadSectionFragments.filter(x => x.sectionFragmentType === SectionFragmentType.Frame);

  const faceFrameIds = faceFrames.map(x => x.id);
  const editFrames = faceFrames.map((f) => makeSectionFragmentMutateViewModel(f, (f as SectionFragmentMutateViewModel)?.deleteMe === true));
  const nonEditSectionFragments = (payloadSectionFragments ?? face?.sectionFragments ?? [])
    .filter(x => faceFrameIds.indexOf(x.id) === -1 && (x as SectionFragmentMutateViewModel)?.deleteMe !== true)
    .map(x => makeSectionFragmentMutateViewModel(x, false, false));

  // Edge case where we didn't touch them but payloadSectionFragments did.
  const passThroughSectionFragments = (payloadSectionFragments ?? face?.sectionFragments ?? [])
    .filter(x => faceFrameIds.indexOf(x.id) === -1 && (x as SectionFragmentMutateViewModel)?.deleteMe === true) // Future add dirty aswell maybe?
    .map(x => makeSectionFragmentMutateViewModel(x, true));

  const frameSurroundingRod = GetSectionFragmentByTypeFromFace(face, SectionFragmentType.FrameSurroundingRod);

  const surroundingRodWidth = getFrameSurroundingRodThickness(frameSurroundingRod, clientVariables);


  const areaFragments: SectionFragmentMutateViewModel[] = payloadSectionFragments === null ?
    GetAreaFragmentsFromFace(face).map(x => makeSectionFragmentMutateViewModel(x, false, false)) :
    payloadSectionFragments?.filter(x => x.sectionFragmentType === SectionFragmentType.AreaFragment)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

  const nonOrphaned: string[] = [];

  areaFragments.forEach((areaFragment, i) => {
    if (areaFragment.deleteMe === true) {
      return;
    }

    const pleaseDontTouchMeThereThatsMyNoNoSquare = !!action.payload.areaFragmentIndexesToRedistribute && action.payload.areaFragmentIndexesToRedistribute.indexOf(i) === -1;

    const areaFragmentFrames = editFrames.filter(x =>
      areaFragment.childSectionFragmentsIds.indexOf(x?.id ?? 'wtfbruh') !== -1)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    const totalFramesWidth = (areaFragment.width) - (surroundingRodWidth * 2);
    const widths: number[] = computeWidths(totalFramesWidth, areaFragmentFrames.length);

    areaFragmentFrames.forEach((aff, i) => {
      const width = widths[i];

      if (!pleaseDontTouchMeThereThatsMyNoNoSquare) {
        aff.width = width;
      }


      aff.sort = i;
      // if(aff.areaType === AreaType.Filler) {
      //   aff.width = DEFAULT_FILLER_WIDTH;
      // } else {
      //   aff.width = widths[i]
      // }
      // x.width = aff.width;


      if (!pleaseDontTouchMeThereThatsMyNoNoSquare) {
        aff.locationX = widths
          .slice(0, i)
          .reduce((sum, mm) => sum + mm, 0);
      }

      aff.dirty = true;
      if (aff.id) {
        nonOrphaned.push(aff.id)
      } else {
        console.error("Could not push non orphan");
      }
    });
  });


  // Delete orphans
  editFrames.forEach(frame => {
    if (!frame?.id || nonOrphaned.indexOf(frame?.id ?? 'no-id') === -1) {
      frame.deleteMe = true;
    }
  });

  yield put(replaceOrderSectionFragmentsAction({
    sectionFragments: [...nonEditSectionFragments, ...passThroughSectionFragments, ...editFrames],
    redistributeFrames: false,
  }));
}


function* ReplaceOrderSectionFragmentsSaga(action: ReturnType<typeof replaceOrderSectionFragmentsAction>) {
  if (action.payload.redistributeFrames) {
    let areaFragmentIndexesToRedistribute: number[] | undefined = undefined;
    if (action.payload.redistributeFrames !== true) {
      try {
        areaFragmentIndexesToRedistribute = action.payload.redistributeFrames as number[];
      } catch (e) {
        // Yes stupid but will work.
        console.log(`unexpected val for redistributeFrames ${e}`)
      }
    }


    yield put(frameRedistributeToAreaFragmentsAction({
      sectionFragments: action.payload.sectionFragments,
      areaFragmentIndexesToRedistribute
    }))
  }
}

function* ChangeAmountOfAreaFragmentFramesSaga(action: ReturnType<typeof changeAmountOfAreaFragmentFramesAction>) {


  const state: DormerRootState = yield select((state: DormerRootState) => state);
  const order: OrderDto | null = state.order.order;
  if (!order) {
    return;
  }
  const clientVariables = getClientConfigurationDormerSpecificSettingsClientVariableSettingsDto(state);

  const faceConfigurationWidth = GetFaceConfigurationWidthFromOrder(order) ?? 0;

  const face = getSouthFaceFromOrder(order);
  if (!face) {
    return;
  }
  const faceId = face.id;

  const currentDefaultAreaFragment = getDefaultAreaFragment(state);
  if (!currentDefaultAreaFragment) {
    return;
  }

  const currentDefaultFrame = getDefaultFrameSelector(state);
  if (!currentDefaultFrame) {
    return;
  }

  const areaFragments = GetAreaFragmentsFromFace(face);

  const areaFragment = areaFragments.find(x => x.id === action.payload.areaFragmentId);

  if (!areaFragment) {
    console.error("Trying to mutate area fragment that dont exist");
    return;
  }

  const areaFragmentChildrenIds = areaFragment.childSectionFragmentsIds;

  const faceFrames = (GetSectionFragmentsByTypeFromFace(face, SectionFragmentType.Frame) as FrameDto[]);

  const foundFaceFrames = faceFrames.filter(x => !!x?.id && areaFragment.childSectionFragmentsIds.indexOf(x?.id ?? '') !== -1);
  const currentAmountOfFrames = foundFaceFrames.length;

  const toCreateAmount = action.payload.amount - currentAmountOfFrames;

  const areaFragmentFrames = faceFrames.filter(x => areaFragmentChildrenIds.indexOf(x.id ?? '') !== -1).sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

  const areaFragmentMutationModel = makeSectionFragmentMutateViewModel(areaFragment, false, true);

  const mutatedSectionFragments: SectionFragmentMutateViewModel[] = [
    // Add non area fragment frames
    ...faceFrames.filter(x => areaFragmentChildrenIds.indexOf(x.id ?? '') === -1).map(x => makeSectionFragmentMutateViewModel(x, false, false)),

    // Add all before current splicing index
    ...areaFragmentFrames.map(x => makeSectionFragmentMutateViewModel(x, false, false)),
  ];

  if (toCreateAmount > 0) {
    for (let i = 0; i < toCreateAmount; i++) {
      const newFrameId = generateGuid();
      const newFrame = createFrameFromDefaultWithId(newFrameId, faceId, currentDefaultFrame, order, (currentAmountOfFrames - 1) + i, clientVariables);

      mutatedSectionFragments.push(makeSectionFragmentMutateViewModel(newFrame));

      // newFrameId
      areaFragmentMutationModel.childSectionFragmentsIds = [...(areaFragmentMutationModel.childSectionFragmentsIds ?? []), newFrameId];
    }
  } else if (toCreateAmount < 0) {

    const removeAmount = -toCreateAmount;
    const removedIds: string[] = [];

    for (let i = 0; i < removeAmount; i++) {
      const removeMe = areaFragmentFrames
        .filter(x => removedIds.indexOf(x.id ?? '') === -1)
        .sort((a, b) => (b.sort ?? 0) - (a.sort ?? 0))
        .find(x => !!x)!;

      if (removeMe && removeMe?.id) {
        mutatedSectionFragments.find(x => x.id === removeMe.id)!
          .deleteMe = true;
        removedIds.push(removeMe!.id!)
        areaFragmentMutationModel.childSectionFragmentsIds = [
          ...((areaFragmentMutationModel.childSectionFragmentsIds ?? []).filter(x => x !== removeMe.id))
        ]
      }
    }
  }


  // const mutatedFrames =

  const windowInsetHeight = getWindowInsetHeight(order, clientVariables);

  mutatedSectionFragments.forEach((x) => {
    x.height = windowInsetHeight;
  });

  // Why tf was this here, it is causing major issues.
  // areaFragmentMutationModel.childSectionFragmentsIds = mutatedSectionFragments
  //   .filter(x=> x.sectionFragmentType === SectionFragmentType.Frame && !!x.id)
  //   .map(x=> x.id!);

  const nonImpactedSectionFragments: SectionFragmentMutateViewModel[] = face.sectionFragments
    .filter(x => x.sectionFragmentType !== SectionFragmentType.Frame)
    .filter(x => x.id !== areaFragmentMutationModel.id)
    .map(x => makeSectionFragmentMutateViewModel(x, false, false));


  const sectionFragments = [...nonImpactedSectionFragments, areaFragmentMutationModel, ...mutatedSectionFragments];

  const newSectionFragmentIds = sectionFragments.map(x => x.id);
  const duplicatIdSectionFragments = sectionFragments.filter(x => newSectionFragmentIds.filter(id => x.id == id).length > 1);

  if (duplicatIdSectionFragments.length > 0) {
    console.error("DUPLICATE ID REPLACEMENT ERR!");
  }


  yield put(replaceOrderSectionFragmentsAction({
    sectionFragments,
    redistributeFrames: true,
  }));
}

function* ApplyPendingWindowInsetFragmentMutationSaga(action: ReturnType<typeof applyPendingWindowInsetFragmentMutationAction>) {
  const state: DormerRootState = yield select((state: DormerRootState) => state);
  const order: OrderDto | null = state.order.order;
  if (!order) {
    return;
  }
  const faceConfigurationWidth = GetFaceConfigurationWidthFromOrder(order) ?? 0;

  const face = getSouthFaceFromOrder(order);
  if (!face) {
    return;
  }

  const faceFrames = (GetSectionFragmentsByTypeFromFace(face, SectionFragmentType.Frame) as FrameDto[])
  const ids = action.payload.pendingMutations.filter(x => x.type === PendingMutationType.windowInset);
  if (!!ids && ids?.length > 1) {
    console.warn("You are trying to mutation more than 1 areafragment at a time, this is unsupported : " + ids.join(", "))
  }

  const windowInsetPendingMigration = ids[0];
  const windowInsetPendingMigrationId = windowInsetPendingMigration?.id;
  if (!windowInsetPendingMigrationId) {
    console.warn("ApplyPendingAreaFragmentMutationSaga called with no mutating ids returning");
    return;
  }

  const faceWindowInsetPendingMigrationDto = faceFrames.find(x => x.id === windowInsetPendingMigrationId);
  if (!faceWindowInsetPendingMigrationDto) {
    return;
  }

  const faceFrameIds = faceFrames.map(x => x.id);
  let editFrames = faceFrames.map((f) => makeSectionFragmentMutateViewModel(f, ((f as any) as SectionFragmentMutateViewModel)?.deleteMe === true));
  const nonEditSectionFragments = (face?.sectionFragments ?? [])
    .filter(x => faceFrameIds.indexOf(x.id) === -1 && (x as SectionFragmentMutateViewModel)?.deleteMe !== true)
    .map(x => makeSectionFragmentMutateViewModel(x, false, false));

  const areaFragments: SectionFragmentMutateViewModel[] = GetAreaFragmentsFromFace(face).map(x => makeSectionFragmentMutateViewModel(x, false, false));

  const frameSurroundingRod = GetSectionFragmentByTypeFromFace(face, SectionFragmentType.FrameSurroundingRod);

  const clientVariables: ClientConfigurationDormerSpecificSettingsClientVariableSettingsDto = yield select(getClientConfigurationDormerSpecificSettingsClientVariableSettingsDto);
  const surroundingRodWidth = getFrameSurroundingRodThickness(frameSurroundingRod, clientVariables);


  areaFragments.forEach((areaFragment) => {
    if (areaFragment.childSectionFragmentsIds.indexOf(windowInsetPendingMigration?.id) === -1) {
      return;
    }

    // const pleaseDontTouchMeThereThatsMyNoNoSquare = !!action.payload.areaFragmentIndexesToRedistribute && action.payload.areaFragmentIndexesToRedistribute.indexOf(i) === -1;

    const areaFragmentFrames = editFrames.filter(x =>
      areaFragment.childSectionFragmentsIds.indexOf(x?.id ?? 'wtfbruh') !== -1)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
    const areaFragmentFrameIds = areaFragmentFrames.map(x => x.id);

    const totalFramesWidth = (areaFragment.width) - (surroundingRodWidth * 2);
    const widths: number[] = computeWidths(totalFramesWidth, areaFragmentFrames.length);

    if (areaFragmentFrameIds.indexOf(windowInsetPendingMigrationId) === -1) { // || !selectableFrame) {
      return;
    }

    const selectableFrame: FrameDto | undefined =
      getSelectableSectionFragmentById(state, windowInsetPendingMigration.copiedFromId ?? '') as FrameDto | undefined;


    const windowInsetPendingMigrationDto = areaFragmentFrames.find(x => x.id === windowInsetPendingMigrationId);
    if (!windowInsetPendingMigrationDto) {
      return;
    }


    const windowInsetPendingMigrationIndex = windowInsetPendingMigrationDto.sort ?? 0;

    const imFineImJustTheWayIAmIAmNotObese = (windowInsetPendingMigration.locationX === null || windowInsetPendingMigration.locationX === undefined) || !windowInsetPendingMigration.width;


    const impactedIndexes: number[] = [];
    areaFragmentFrames.forEach((aff, index) => {
      const selectableFrame: FrameDto | undefined = aff.id !== windowInsetPendingMigration.id ? undefined : getSelectableSectionFragmentById(state, windowInsetPendingMigration.copiedFromId ?? '') as FrameDto | undefined;

      const width = widths[index];

      // if(aff.id !== windowInsetPendingMigrationId ){ // || !selectableFrame) {
      //   return;
      // }

      const i = index;

      const unmodifiedRequestedMutationFor = areaFragmentFrames.find(x => x.id === windowInsetPendingMigration.id);
      if (!unmodifiedRequestedMutationFor) {
        console.error("unmodifiedRequestedMutationFor not found!");
        return;
      }

      const markCurrentImpactedIndex = () => impactedIndexes.push(i);


      // How to check if we are impacted.
      // We need to check if we overlap.
      const pendingMutationStart = imFineImJustTheWayIAmIAmNotObese ? aff.locationX : windowInsetPendingMigration.locationX ?? 0;
      const pendingMutationWidth = imFineImJustTheWayIAmIAmNotObese ? aff.width : windowInsetPendingMigration.width ?? 0;
      const pendingMutationEnd = pendingMutationStart + pendingMutationWidth;


      const currentAfStart = aff.locationX;
      const currentAfEnd = aff.locationX + aff.width;
      const currentAfWidth = aff.width;


      const shrinkInPlace = unmodifiedRequestedMutationFor.locationX < pendingMutationStart &&
        unmodifiedRequestedMutationFor.width > pendingMutationWidth;

      if (imFineImJustTheWayIAmIAmNotObese === false) {

        // This region should not be impacted by modificiations, past currentAfEnd(in case 3 afsf), and is leftmost
        //
        // {
        if (windowInsetPendingMigrationIndex > i) {
          // Impacted on right end check, shrinking current iterator
          if (currentAfEnd > pendingMutationStart) {
            // Now we are overlapping on the rightmost side.

            // This should results off the amount of diffrence to offset, will most likely be negative if were going over the space boundry.
            // And growing the other thereby shrinking current mm (iterator index)
            const overlappingBy = pendingMutationStart - currentAfEnd;
            aff.width += overlappingBy;
            markCurrentImpactedIndex();
          }
          // Impacted on right end check, enlarging current iterator
          const diffOnCurrentAfEnd = pendingMutationStart - currentAfEnd;
          if (diffOnCurrentAfEnd > 1 && shrinkInPlace && (i + 1 === windowInsetPendingMigrationIndex)) {
            // This should give us the amount we have left over at the end of the current iterator
            const widthToAdd = diffOnCurrentAfEnd;
            aff.width += widthToAdd;
            markCurrentImpactedIndex();
          }
        } else if (windowInsetPendingMigrationIndex === i) {
          aff.width = pendingMutationWidth;
          aff.locationX = pendingMutationStart
          markCurrentImpactedIndex();

        } else if (windowInsetPendingMigrationIndex < i) {

          // Making current smaller.
          if (pendingMutationEnd > currentAfStart) {
            const diff = pendingMutationEnd - currentAfStart;
            aff.width -= diff;
            aff.locationX += diff;
            markCurrentImpactedIndex();

            // Making current bigger, the above clause could be made the same but that would make it less ledgible for future developers looking at this
          } else if (
            pendingMutationEnd < currentAfStart &&
            (
              // In case of growing over an entire areafragment
              (pendingMutationStart > currentAfStart) ||
              // In case we are next to the current modifying, otherwise we will calcculate a diff with no overlap
              (i - 1 === windowInsetPendingMigrationIndex)
            )
          ) {
            const diff = currentAfStart - pendingMutationEnd;
            aff.width += diff;
            aff.locationX -= diff;
            markCurrentImpactedIndex();
          }
        }
      }

      const windowInsetHeight = getWindowInsetHeight(order, clientVariables);

      const editMe = {
        ...aff,
        ...selectableFrame,
        id: aff.id,
        tempId: aff?.tempId ?? generateGuid(),
        copiedFrom: (selectableFrame as SectionFragmentDto | null ?? aff?.copiedFrom),
        copiedFromId: (selectableFrame?.id ?? aff?.copiedFromId)!,
        sort: i,
        organisationId: null,
        orderId: order.id,
        configurationTemplateId: null,
        faceConfigurationId: order.faceConfiguration.id,
        isOrganisationAddable: false,
        isDefaultForTemplate: false,
        isUserSelectable: false,
        belongsToUser: true,
        faceId: aff.faceId,
        variation: (selectableFrame?.variation ?? aff.variation)!,
        width: aff.width,
        height: windowInsetHeight,
        locationX: aff.locationX
      }

      // aff.locationX = widths
      //   .slice(0, i)
      //   .reduce((sum, mm) => sum + mm, 0);
      if (editMe.areaType === AreaType.Filler) {
        editMe.width = DEFAULT_FILLER_WIDTH;
      } else {
        // editMe.width = widths[i]
      }

      editMe.dirty = true;


      if (editMe.id) {
        // nonOrphaned.push(aff.id)
      } else {
        console.error("Could not push non orphan");
      }

      editFrames = [editMe, ...editFrames.filter(x => x.id !== aff.id)]
    });


  });


  yield put(replaceOrderSectionFragmentsAction({
    sectionFragments: [...nonEditSectionFragments, ...editFrames],
    redistributeFrames: false,
  }));

}


function* ApplyPendingAreaFragmentMutationSaga(action: ReturnType<typeof applyPendingAreaFragmentMutationAction>) {
  const state: DormerRootState = yield select((state: DormerRootState) => state);
  const order: OrderDto | null = state.order.order;
  if (!order) {
    return;
  }

  const face = getSouthFaceFromOrder(order);
  if (!face) {
    return;
  }
  const faceId = face.id;

  const minFrameWidth = 200; //mm

  const determinedWidths: number[] = [];

  const areaFragments = GetAreaFragmentsFromFace(face);

  const pendingMutations = action.payload.pendingMutations.filter(x => x.type === PendingMutationType.areaFragment);

  if (!!pendingMutations && pendingMutations?.length > 1) {
    console.warn("You are trying to mutation more than 1 areafragment at a time, this is unsupported : " + pendingMutations.join(", "))
  }

  const areaFragmentPendingMigration = pendingMutations[0];
  const areaFragmentPendingMigrationId = areaFragmentPendingMigration?.id;
  if (!areaFragmentPendingMigrationId) {
    console.warn("ApplyPendingAreaFragmentMutationSaga called with no mutating ids returning");
    return;
  }

  const areaFragmentPendingMigrationDto = areaFragments.find(x => x.id === areaFragmentPendingMigrationId);
  if (!areaFragmentPendingMigrationDto) {
    return;
  }


  if (areaFragmentPendingMigration.locationX != null && areaFragmentPendingMigration.width != null) {


    const mutatedModels: SectionFragmentMutateViewModel[] = areaFragments.map(af => {

      // Lets do all dirty for now, working first nice second
      return makeSectionFragmentMutateViewModel(af, false, true);
    }).sort((a, b) => (a.sort ?? -1) - (b.sort ?? -1));

    const areaFragmentPendingMutationIndex = areaFragmentPendingMigrationDto.sort ?? 0;

    const impactedIndexes: number[] = [];

    const markImpacted = (index: number) => {
      const sortIndex = mutatedModels[index]?.sort ?? index;
      impactedIndexes.push(sortIndex);
    };

    const pendingMutationStart = areaFragmentPendingMigration.locationX!;
    const pendingMutationWidth = areaFragmentPendingMigration.width!;
    const pendingMutationEnd = pendingMutationStart + pendingMutationWidth;

    const unmodifiedRequestedMutationFor = mutatedModels.find(x => x.id === areaFragmentPendingMigration.id);
    if (!unmodifiedRequestedMutationFor) {
      console.error("unmodifiedRequestedMutationFor not found!");
      return;
    }

    const originalMutationStart = unmodifiedRequestedMutationFor.locationX;
    const originalMutationEnd = unmodifiedRequestedMutationFor.locationX + unmodifiedRequestedMutationFor.width;

    const mutatingModel = mutatedModels[areaFragmentPendingMutationIndex];
    if (!mutatingModel) {
      console.error("mutatingModel not found for areaFragmentPendingMutationIndex", areaFragmentPendingMutationIndex);
      return;
    }

    mutatingModel.locationX = pendingMutationStart;
    mutatingModel.width = pendingMutationWidth;
    markImpacted(areaFragmentPendingMutationIndex);

    let leftDelta = pendingMutationStart - originalMutationStart;
    for (let leftIndex = areaFragmentPendingMutationIndex - 1; leftIndex >= 0 && leftDelta !== 0; leftIndex--) {
      const leftFragment = mutatedModels[leftIndex];
      if (leftDelta > 0) {
        leftFragment.width += leftDelta;
        markImpacted(leftIndex);
        leftDelta = 0;
      } else {
        const overlap = Math.min(leftFragment.width, Math.abs(leftDelta));
        if (overlap === 0) {
          continue;
        }
        leftFragment.width -= overlap;
        markImpacted(leftIndex);
        leftDelta += overlap;
      }
    }

    let rightDelta = pendingMutationEnd - originalMutationEnd;
    for (let rightIndex = areaFragmentPendingMutationIndex + 1; rightIndex < mutatedModels.length && rightDelta !== 0; rightIndex++) {
      const rightFragment = mutatedModels[rightIndex];
      if (rightDelta > 0) {
        const overlap = Math.min(rightFragment.width, rightDelta);
        if (overlap === 0) {
          continue;
        }
        rightFragment.width -= overlap;
        rightFragment.locationX += overlap;
        markImpacted(rightIndex);
        rightDelta -= overlap;
      } else {
        const gapFill = Math.abs(rightDelta);
        rightFragment.width += gapFill;
        rightFragment.locationX -= gapFill;
        markImpacted(rightIndex);
        rightDelta += gapFill;
      }
    }

    const otherSectionFragments = face.sectionFragments
      .filter(x => x.sectionFragmentType !== SectionFragmentType.AreaFragment)
      .map(x => makeSectionFragmentMutateViewModel(x, false, false));

    const sectionFragments = [
      ...otherSectionFragments,
      ...mutatedModels
    ];
    yield put(replaceOrderSectionFragmentsAction({
      sectionFragments,
      redistributeFrames: impactedIndexes,
    }));
  }

  if (areaFragmentPendingMigration.amountOfSurfaces != null) {
    yield put(changeAmountOfAreaFragmentFramesAction({
      amount: areaFragmentPendingMigration.amountOfSurfaces,
      areaFragmentId: areaFragmentPendingMigration.id
    }));
  }
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

function* ApplyMostSuitableAreaFragmentCombinationDeclarationGroupSaga(action: ReturnType<typeof applyMostSuitableAreaFragmentCombinationDeclarationGroupAction>) {
  let count = 0;

  for (; ;) {
    yield call(sleep, 1);
    const af: AreaFragmentDto = yield select(getDefaultAreaFragment);
    if (af != null) {
      break;
    }

    if (count > 2000) {
      break;
    }

    count++;
  }


  const selectedConfigurationTemplate: ConfigurationTemplateDto | null = yield select(getSelectedConfigurationTemplateSelector);
  let groups: AreaFragmentCombinationDeclarationGroupDto[] = [...selectedConfigurationTemplate?.templateFaceConfiguration.areaFragmentCombinationDeclarationGroups ?? []];
  groups = groups.sort((a, b) => (b?.maxWidth ?? 0) - (a?.maxWidth ?? 0));

  if (groups.length === 0) {
    console.error('no AreaFragmentCombinationDeclarationGroupDto exist for this order');
    return;
  }

  const faceConfiguration: FaceConfigurationDto = yield select(getFaceConfiguration);
  const faceConfigurationWidth = faceConfiguration?.width;

  const suitableForConfigurationWidth = groups
    .filter(x => x.maxWidth >= (faceConfigurationWidth ?? 0) && x.minWidth <= (faceConfigurationWidth ?? 999999))
    .sort((a, b) => b.maxWidth - a.maxWidth);

  const putMeDaddy = suitableForConfigurationWidth.find(x => x);

  if (putMeDaddy) {
    yield put(applySelectedAreaFragmentCombinationDeclarationGroupAction({areaFragmentCombinationDeclarationGroupId: putMeDaddy.id}))
  }
}


function* ApplySelectedAreaFragmentCombinationDeclarationGroupSaga(action: ReturnType<typeof applySelectedAreaFragmentCombinationDeclarationGroupAction>) {
  const state: DormerRootState = yield select((state: DormerRootState) => state);
  const order: OrderDto | null = state.order.order;
  if (!order) {
    return;
  }

  const face = getSouthFaceFromOrder(order);
  if (!face) {
    return;
  }

  let mutatedSectionFragments: SectionFragmentMutateViewModel[] = []
  const faceConfigurationWidth = GetFaceConfigurationWidthFromOrder(order) ?? 0;
  const totalWidthOfNonAreaFragments = GetTotalWidthOfNonAreaFragments(face) ?? 0;


  let totalAreaFragmentWidth = faceConfigurationWidth - totalWidthOfNonAreaFragments;


  const faceId = face.id;

  const currentDefaultAreaFragment = getDefaultAreaFragment(state);
  if (!currentDefaultAreaFragment) {
    return;
  }

  const currentDefaultFrame = getDefaultFrameSelector(state);
  if (!currentDefaultFrame) {
    return;
  }

  const deletemeAreaFragments = GetAreaFragmentsFromFace(face);
  const deleteFrames = GetSectionFragmentsByTypeFromFace(face, SectionFragmentType.Frame);

  const selectedConfigurationTemplate: ConfigurationTemplateDto | null = getSelectedConfigurationTemplateSelector(state);


  const groups: AreaFragmentCombinationDeclarationGroupDto[] = _.clone(selectedConfigurationTemplate?.templateFaceConfiguration.areaFragmentCombinationDeclarationGroups) ?? [];

  const selectedGroup = groups.find(x => x.id === action.payload.areaFragmentCombinationDeclarationGroupId);
  if (!selectedGroup) {
    console.error('selectedgroup does not exist for id: ' + action.payload.areaFragmentCombinationDeclarationGroupId);
    return;
  }

  const totalAreaFragmentFixedWidth = selectedGroup?.areaFragmentCombinationDeclarations
    .map(x => (x?.fixedWidth ?? 0))
    .reduce((a, b) => a + b);

  totalAreaFragmentWidth -= totalAreaFragmentFixedWidth;

  // lets start off by just deleting all existing, since we are starting from scratch.
  mutatedSectionFragments = [...deletemeAreaFragments, ...deleteFrames].map(x => makeSectionFragmentMutateViewModel(x, true));


  const flexTotalForAllAreaFragments = selectedGroup.areaFragmentCombinationDeclarations.map(x => x.flex ?? 0).reduce((acc, pos) => acc + pos, 0);

  const clientVariables: ClientConfigurationDormerSpecificSettingsClientVariableSettingsDto = yield select(getClientConfigurationDormerSpecificSettingsClientVariableSettingsDto);
  const frameSurroundingRod = GetSectionFragmentByTypeFromFace(face, SectionFragmentType.FrameSurroundingRod);
  const surroundingRodWidth = getFrameSurroundingRodThickness(frameSurroundingRod, clientVariables);

  const brandNewAreaFragments: SectionFragmentMutateViewModel[] = [];
  const brandNewFrames: SectionFragmentMutateViewModel[] = [];

  const areaFragmentWidths: number[] = [];
  const flexibleIndices: number[] = [];
  const flexibleFlexes: number[] = [];
  selectedGroup.areaFragmentCombinationDeclarations.forEach((afcd, i) => {
    if (afcd.fixedWidth != null) {
      areaFragmentWidths[i] = afcd.fixedWidth;
    } else {
      flexibleIndices.push(i);
      flexibleFlexes.push(afcd.flex ?? 0);
    }
  });
  const computedFlexibleWidths = computeProportionalWidths(totalAreaFragmentWidth, flexibleFlexes);
  flexibleIndices.forEach((i, j) => {
    areaFragmentWidths[i] = computedFlexibleWidths[j];
  });

  selectedGroup.areaFragmentCombinationDeclarations.forEach((afcd, idx) => {
    let currentAreaFragmentWidth = areaFragmentWidths[idx];
    const isForcedFixedWidthByOnlyOnlyChildrenWithFixedWidth = (
      afcd.windowInsetCombinationDeclarations.length === afcd.windowInsetCombinationDeclarations.filter(x => x.fixedWidth != null).length
    );

    const fixedCurrentAreaFragmentWidth = afcd.windowInsetCombinationDeclarations
      .map(x => (x.fixedWidth ?? 0))
      .reduce((a, b) => a + b);

    let nonFixedCurrentAreaFragmentWidth = currentAreaFragmentWidth - fixedCurrentAreaFragmentWidth - surroundingRodWidth * 2;


    if (isForcedFixedWidthByOnlyOnlyChildrenWithFixedWidth) {
      // Cap on area fragment width, dont let the frame decide parents width:
      if(fixedCurrentAreaFragmentWidth + surroundingRodWidth * 2 >= (afcd?.fixedWidth ?? 9999)) {
        currentAreaFragmentWidth = afcd?.fixedWidth ?? 0;
        if(nonFixedCurrentAreaFragmentWidth < 0) {
          nonFixedCurrentAreaFragmentWidth = 0;
        }
      } else {
        currentAreaFragmentWidth = fixedCurrentAreaFragmentWidth + surroundingRodWidth * 2;
      }
    }

    const newAreaFragmentId = generateGuid();
    const newAreaFragment: AreaFragmentDto = createAreaFragmentFromDefaultWithId(
      currentDefaultAreaFragment,
      newAreaFragmentId,
      order,
      afcd.sort,
      faceId
    );
    newAreaFragment.width = currentAreaFragmentWidth;


    const xOffset = Math.round((getEastSplinterWidth(face) ?? 0) +
      (getCheeksWidth(face, SectionFragmentSide.East) ?? 0));

    newAreaFragment.locationX = brandNewAreaFragments
      .map(x => x.width)
      .slice(0, afcd.sort)
      .reduce((sum, mm) => sum + mm, 0);
    newAreaFragment.locationX += xOffset;

    const nonFixedInsets = afcd.windowInsetCombinationDeclarations.filter(win => win.fixedWidth == null);
    const computedWindowInsetWidths = nonFixedInsets.length > 0 ? computeProportionalWidths(nonFixedCurrentAreaFragmentWidth, nonFixedInsets.map(win => win.flex ?? 0)) : [];
    let nonFixedIndex = 0;
    const frameWidths: number[] = [];
    const sortedInsets = afcd.windowInsetCombinationDeclarations;
    // sortedInsets = sortedInsets.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    sortedInsets.forEach((windowInsetDeclaration, i) => {
      const windowInsetFlex = windowInsetDeclaration.flex ?? 0;
      const currentWindowInsetWidth = windowInsetDeclaration.fixedWidth != null ? windowInsetDeclaration.fixedWidth : computedWindowInsetWidths[nonFixedIndex++];
      const newFrameId = generateGuid();

      const defaultWindowInsetToUse: FrameDto = (state.selectableSectionFragments.selectableSectionFragments?.find(x => x.id === windowInsetDeclaration.copiedFromId) ?? currentDefaultFrame) as FrameDto;

      const newFrame = createFrameFromDefaultWithId(newFrameId, faceId, defaultWindowInsetToUse, order, windowInsetDeclaration.sort, clientVariables);
      newFrame.width = currentWindowInsetWidth;
      frameWidths.push(newFrame.width);
      newFrame.locationX = frameWidths
        .slice(0, windowInsetDeclaration.sort)
        .reduce((sum, mm) => sum + mm, 0);
      newAreaFragment.childSectionFragments.push(newFrame);
      newAreaFragment.childSectionFragmentsIds.push(newFrameId);

      brandNewFrames.push(makeSectionFragmentMutateViewModel(newFrame, false, true));

    });

    brandNewAreaFragments.push(makeSectionFragmentMutateViewModel(newAreaFragment, false, true));
  });

  const nonImpactedSectionFragments: SectionFragmentMutateViewModel[] = face.sectionFragments
    .filter(x =>
      x.sectionFragmentType !== SectionFragmentType.Frame &&
      x.sectionFragmentType !== SectionFragmentType.AreaFragment)
    .map(x => makeSectionFragmentMutateViewModel(x, false, false));

  // Debug: Detect preserved splinters in layout application
  const preservedSplinters = nonImpactedSectionFragments.filter(x =>
    x.sectionFragmentType === SectionFragmentType.Splinter
  );

  if (preservedSplinters.length > 0) {
    console.warn('⚠️ PRESERVING EXISTING SPLINTERS IN LAYOUT APPLICATION', {
      count: preservedSplinters.length,
      splinters: preservedSplinters.map(s => ({
        id: s.id,
        splinterSide: (s as any).splinterSide,
        splinterSideString: (s as any).splinterSide != null ? SectionFragmentSide[(s as any).splinterSide] : 'UNDEFINED',
        width: s.width,
        height: s.height
      }))
    });
  }

  // const areaFragmentsGpt = MakeGptInspectable(brandNewAreaFragments);


  const all: SectionFragmentMutateViewModel[] = [...brandNewAreaFragments, ...brandNewFrames, ...mutatedSectionFragments, ...nonImpactedSectionFragments];


  const windowInsetHeight = getWindowInsetHeight(order, clientVariables);
  const frames = all.filter(x =>
    x.sectionFragmentType === SectionFragmentType.Frame &&
    x.belongsToUser === true &&
    x.deleteMe === false
  );
  frames.map(x => {
    if (x.height != windowInsetHeight) {
      x.height = windowInsetHeight;
      x.dirty = true;
    }
  })

  // const framesGpt = MakeGptInspectable(frames);


  yield put(replaceOrderSectionFragmentsAction({
    sectionFragments: all
  }));
}


export function* AreaFragmentSagas() {
  yield takeLatest(changeAmountOfAreaFragmentAction, ChangeAmountAreaFragmentSaga);
  yield takeLatest(replaceOrderSectionFragmentsAction, ReplaceOrderSectionFragmentsSaga);


  // MutateAreaFragments
  yield takeLatest(applyPendingAreaFragmentMutationAction, ApplyPendingAreaFragmentMutationSaga);


  // applySelectedAreaFragmentCombinationDeclarationGroup
  yield takeLatest(applySelectedAreaFragmentCombinationDeclarationGroupAction, ApplySelectedAreaFragmentCombinationDeclarationGroupSaga);
  yield takeLatest(applyMostSuitableAreaFragmentCombinationDeclarationGroupAction, ApplyMostSuitableAreaFragmentCombinationDeclarationGroupSaga);

  // TODO: Move to frames
  yield takeLatest(frameRedistributeToAreaFragmentsAction, FrameRedistributeToAreaFragmentsSaga);

  yield takeLatest(changeAmountOfAreaFragmentFramesAction, ChangeAmountOfAreaFragmentFramesSaga);


  yield takeLatest(applyPendingWindowInsetFragmentMutationAction, ApplyPendingWindowInsetFragmentMutationSaga);


}
